import PropTypes from 'prop-types';
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { useTimeoutFn } from 'react-use';

import { selectShopEventProducts } from '@selectors/shopEvent';

import { SHOP_EVENT_AVAILABILITY_LABEL } from '@common/constants/shopEvent';
import LayoutSwitcher from '@common/components/LayoutSwitcher';
import ShopEventProductBox from '@common/components/ShopEventProductBox';
import ProductStickyContainer from '@common/components/ProductStickyContainer';
import { ProductWrapper } from '@common/components/ProductsGrid/ProductsGrid.styled';

import {
  DividerContainer,
  ProductsGridStyled,
} from './ShopEventProductList.styled';

const ShopEventProductList = ({
  divider,
  stickyContainerBottomSpacing,
  flashSaleMode,
}) => {
  const products = useSelector(selectShopEventProducts());
  const productsGridRef = useRef(null);

  const animationOrder = useMemo(
    () =>
      products
        .reduce((acc, { productColorId, availabilityLabel }) => {
          if (availabilityLabel === SHOP_EVENT_AVAILABILITY_LABEL.LOW) {
            acc.push(productColorId);
          }

          return acc;
        }, [])
        .sort(() => Math.random() - 0.5),
    [products],
  );
  const [animatingProductId, setAnimatingProductId] = useState(
    animationOrder[0],
  );
  const [elementsVisibility, setElementsVisibility] = useState({});

  const handleLowLabelIntersection = useCallback(
    (productColorId, shouldAnimate) => {
      setElementsVisibility(prevState => ({
        ...prevState,
        [productColorId]: shouldAnimate,
      }));
    },
    [setElementsVisibility],
  );

  const findNextAnimatingProductId = startIndex =>
    animationOrder
      .slice(startIndex < animationOrder.length ? startIndex : 0)
      .find(productColorId => elementsVisibility[productColorId]);

  const setNextAnimatingProductId = () => {
    const startIndex = animationOrder.indexOf(animatingProductId) + 1;

    let nextAnimatingProductId = findNextAnimatingProductId(startIndex);

    if (
      startIndex !== 0 &&
      startIndex < animationOrder.length &&
      typeof nextAnimatingProductId === 'undefined'
    ) {
      nextAnimatingProductId = findNextAnimatingProductId(0);
    }

    if (nextAnimatingProductId === animatingProductId) {
      setAnimatingProductId(undefined);
    }
    setTimeout(() => setAnimatingProductId(nextAnimatingProductId), 0);
  };

  const [animationIsReady, animationCancel, animationRestart] = useTimeoutFn(
    () => {
      setNextAnimatingProductId();
      animationRestart();
    },
    2500,
  );

  useEffect(() => {
    const isAnimationElementVisible =
      Object.values(elementsVisibility).includes(true);
    const animationState = animationIsReady();

    if (animationState === null && isAnimationElementVisible) {
      setNextAnimatingProductId();
      animationRestart();
    }

    if (!isAnimationElementVisible) {
      animationCancel();
    }
  }, [animatingProductId, elementsVisibility]);

  return (
    <React.Fragment>
      <ProductsGridStyled
        data-testid="shopEventProductList"
        ref={productsGridRef}
      >
        {products.map(
          (
            {
              availabilityLabel,
              discountPercentage,
              title,
              specialPrice,
              slug,
              productColorId,
              price,
              image,
              colorTitle,
            },
            index,
          ) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`${productColorId}-${index}`}>
              <ProductWrapper>
                <ShopEventProductBox
                  image={image}
                  price={price}
                  productColorId={productColorId}
                  slug={slug}
                  specialPrice={specialPrice}
                  title={title}
                  colorTitle={colorTitle}
                  availabilityLabel={availabilityLabel}
                  discountPercentage={discountPercentage}
                  availabilityLabelShouldAnimate={
                    animatingProductId === productColorId
                  }
                  onAvailabilityLabelIntersection={handleLowLabelIntersection}
                />
              </ProductWrapper>
              {flashSaleMode &&
                (index + 1) % 12 === 0 &&
                products.length - (index + 1) >= 4 && (
                  <DividerContainer>{divider}</DividerContainer>
                )}
            </React.Fragment>
          ),
        )}
      </ProductsGridStyled>

      <ProductStickyContainer
        productsGridRef={productsGridRef}
        bottomSpacing={stickyContainerBottomSpacing ? '7rem' : '0'}
      >
        {products.length > 0 && <LayoutSwitcher />}
      </ProductStickyContainer>
    </React.Fragment>
  );
};

ShopEventProductList.propTypes = {
  divider: PropTypes.node.isRequired,
  stickyContainerBottomSpacing: PropTypes.bool,
  flashSaleMode: PropTypes.bool.isRequired,
};

ShopEventProductList.defaultProps = {
  stickyContainerBottomSpacing: false,
};

export default ShopEventProductList;
